import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { colors } from '@/constants/colors';
import IconsLib from '@/utils/IconsLib';
import { IconType } from '@/utils/icons';
import { Surface, useSurfaceStore } from '@/store/useSurfaceStore';
import { ISP_ENABLED } from '@/pages/Admin/Isp/ispEnabled';

interface Option {
   id: Surface;
   label: string;
   icon: IconType;
   /** Default page to land on when the user picks this surface. */
   defaultPath: string;
   disabled?: boolean;
   note?: string;
   /** Short product tag shown as a pill beside the label. */
   tag?: string;
}

const OPTIONS: Option[] = [
   // "v2.0" told an operator nothing — it was our release number, not a
   // product. Hybrid is the product they are choosing (Ray, 2026-08-13).
   { id: 'wifi', label: 'Piso WiFi', icon: 'router-fill', defaultPath: '/wifi', tag: 'Hybrid' },
   { id: 'voucher-store', label: 'Voucher Store', icon: 'store-3-fill', defaultPath: '/voucher' },
   // Vendo Tab was listed here as a greyed-out "Coming soon" row and removed
   // 2026-08-30 (Ray). A product nobody can pick is a dead line in a menu the
   // operator opens to switch products. The `tab` surface itself still exists
   // in the store, so a saved one keeps resolving — it just cannot be chosen.
   // The operator's OWN internet business: they run PPPoE on their MikroTik and
   // sell connections to homes and shops. A different product from Piso WiFi,
   // sharing the same login and the same router tunnel.
   {
      id: 'isp',
      label: 'ISP Business',
      icon: 'global-fill',
      defaultPath: '/isp',
      tag: 'Beta',
   },
];

const MENU_WIDTH = 200;
const MENU_GAP = 6;

const SurfaceSwitcher = () => {
   const { surface, setSurface } = useSurfaceStore();
   const navigate = useNavigate();
   const [open, setOpen] = useState(false);
   const [menuAt, setMenuAt] = useState({ top: 0, left: 0 });
   const wrapperRef = useRef<HTMLDivElement>(null);

   useEffect(() => {
      if (!open) return;
      const onDocClick = (e: MouseEvent) => {
         if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
      };
      // Turning a phone moves the button out from under the menu.
      const onResize = () => setOpen(false);
      document.addEventListener('mousedown', onDocClick);
      window.addEventListener('resize', onResize);
      return () => {
         document.removeEventListener('mousedown', onDocClick);
         window.removeEventListener('resize', onResize);
      };
   }, [open]);

   const toggle = () => {
      const box = wrapperRef.current?.getBoundingClientRect();
      if (box) {
         setMenuAt({
            top: box.bottom + MENU_GAP,
            left: Math.max(
               MENU_GAP,
               Math.min(box.left, window.innerWidth - MENU_WIDTH - MENU_GAP)
            ),
         });
      }
      setOpen((v) => !v);
   };

   // ISP Business is hidden unless this build turns it on — it is in production
   // dark, so staging could be handed to another session mid-build.
   const options = OPTIONS.filter((o) => o.id !== 'isp' || ISP_ENABLED);
   const current = options.find((o) => o.id === surface) ?? options[0];

   return (
      <Wrapper ref={wrapperRef}>
         <Trigger type="button" onClick={toggle} $open={open}>
            <IconsLib icon={current.icon} size="16px" color={colors.PRIMARY} />
            <TriggerLabel>{current.label}</TriggerLabel>
            {current.tag && <TriggerPill>{current.tag}</TriggerPill>}
            <IconsLib
               icon="arrow-down-line"
               size="16px"
               color={colors.GRAY1}
            />
         </Trigger>
         {open && (
            <Menu role="listbox" style={menuAt}>
               {options.map((opt) => {
                  const active = opt.id === surface;
                  return (
                     <MenuItem
                        key={opt.id}
                        role="option"
                        aria-selected={active}
                        aria-disabled={opt.disabled || undefined}
                        $active={active}
                        $disabled={!!opt.disabled}
                        title={opt.disabled ? opt.note : undefined}
                        onClick={() => {
                           if (opt.disabled) return;
                           setSurface(opt.id);
                           setOpen(false);
                           if (opt.id !== surface) navigate(opt.defaultPath);
                        }}
                     >
                        <IconsLib
                           icon={opt.icon}
                           size="18px"
                           color={
                              opt.disabled
                                 ? colors.GRAY12
                                 : active
                                 ? colors.PRIMARY
                                 : colors.GRAY1
                           }
                        />
                        <MenuLabel $active={active} $disabled={!!opt.disabled}>
                           {opt.label}
                        </MenuLabel>
                        {opt.tag && <BetaPill>{opt.tag}</BetaPill>}
                        {opt.note && <Note>{opt.note}</Note>}
                     </MenuItem>
                  );
               })}
            </Menu>
         )}
      </Wrapper>
   );
};

const Wrapper = styled.div`
   position: relative;
   /* Never narrower than the icon and the arrow, or there is nothing to tap. */
   min-width: 56px;
`;

const Trigger = styled.button<{ $open: boolean }>`
   display: inline-flex;
   align-items: center;
   gap: 8px;
   padding: 6px 10px;
   border-radius: 8px;
   border: 1px solid
      ${({ $open }) => ($open ? colors.PRIMARY : colors.GRAY13)};
   background: ${({ $open }) =>
      $open ? colors.BLUE_SUPER_LIGHT : 'var(--app-surface)'};
   cursor: pointer;
   transition: background 0.15s, border-color 0.15s;
   /* Fit the room the phone header leaves: the name shortens, the arrow stays. */
   max-width: 100%;
   min-width: 0;
   > * {
      flex-shrink: 0;
   }
   &:hover {
      background: ${colors.BLUE_SUPER_LIGHT};
      border-color: ${colors.PRIMARY};
   }
   @media (max-width: 768px) {
      gap: 6px;
      padding: 6px 8px;
   }
`;

const TriggerLabel = styled.span`
   font-size: 13px;
   font-weight: 600;
   color: ${colors.MAIN_DARK90};
   && {
      flex-shrink: 1;
   }
   min-width: 0;
   overflow: hidden;
   text-overflow: ellipsis;
`;

// 🔴 FIXED, not absolute. On phones the header's left side has overflow: hidden
// so a long name cannot push the profile button off-screen, and that also cut
// off a menu hanging below it: tapping the button opened nothing you could see.
const Menu = styled.div`
   position: fixed;
   min-width: ${MENU_WIDTH}px;
   background: var(--app-surface);
   border: 1px solid ${colors.GRAY13};
   border-radius: 10px;
   box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
   padding: 4px;
   z-index: 1100;
`;

const MenuItem = styled.div<{ $active: boolean; $disabled: boolean }>`
   display: flex;
   align-items: center;
   gap: 8px;
   padding: 8px 10px;
   border-radius: 6px;
   cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
   background: ${({ $active }) =>
      $active ? colors.BLUE_SUPER_LIGHT : 'transparent'};
   opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};
   &:hover {
      background: ${({ $disabled, $active }) =>
         $disabled
            ? $active
               ? colors.BLUE_SUPER_LIGHT
               : 'transparent'
            : colors.GRAY14};
   }
`;

const MenuLabel = styled.span<{ $active: boolean; $disabled: boolean }>`
   font-size: 13px;
   font-weight: ${({ $active }) => ($active ? 600 : 500)};
   color: ${({ $disabled, $active }) =>
      $disabled
         ? colors.GRAY1
         : $active
         ? colors.PRIMARY
         : colors.MAIN_DARK90};
   flex: 1;
`;

const Note = styled.span`
   font-size: 10px;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.05em;
   color: ${colors.GRAY1};
   background: ${colors.GRAY14};
   padding: 2px 6px;
   border-radius: 4px;
`;

const BetaPill = styled.span`
   padding: 2px 6px;
   background: linear-gradient(135deg, #f59e0b, #f97316);
   color: #fff;
   font-size: 9px;
   font-weight: 800;
   letter-spacing: 0.6px;
   line-height: 1;
   border-radius: 4px;
   text-transform: uppercase;
`;

/* The menu names the tag anyway; on a phone the button needs the room. */
const TriggerPill = styled(BetaPill)`
   @media (max-width: 768px) {
      display: none;
   }
`;

export default SurfaceSwitcher;
